import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, fmtEUR } from "@/lib/api";
import NewProjectDialog from "@/components/NewProjectDialog";
import { ArrowUpRight, Building2, FileStack, AlertTriangle, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const STATUS_LABEL = {
    consultation: "En consultation",
    en_cours: "En cours",
    termine: "Terminé",
    archive: "Archivé",
};

export default function ProjectsPage() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const data = await api.listProjects();
            setProjects(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div className="min-h-screen">
            {/* Hero */}
            <header
                className="border-b border-border bg-white"
                data-testid="projects-hero"
            >
                <div className="max-w-[1400px] mx-auto px-10 py-12 flex items-end justify-between gap-8">
                    <div className="max-w-2xl">
                        <div className="stat-label mb-3">
                            01 — Tableau de bord
                        </div>
                        <h1 className="font-heading font-extrabold text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                            Comparez vos devis,
                            <br />
                            <span className="text-accent">
                                traquez les doublons.
                            </span>
                        </h1>
                        <p className="mt-5 text-ink-soft text-base max-w-xl leading-relaxed">
                            Importez les devis PDF de vos artisans. L'IA extrait
                            les postes, détecte les chevauchements entre corps
                            de métier (terrassier vs plombier, électricien vs
                            maçon…) et vous prépare des arguments de
                            négociation.
                        </p>
                        <div className="mt-7 flex gap-3">
                            <NewProjectDialog onCreated={load} />
                            <a
                                href="#chantiers"
                                className="rounded-none border border-ink px-5 py-2 text-sm font-medium hover:bg-ink hover:text-white transition-colors"
                            >
                                Voir les chantiers
                            </a>
                        </div>
                    </div>
                    <div className="hidden lg:block">
                        <div className="border border-ink p-6 bg-[#FAFAFA] w-[300px]">
                            <div className="flex items-center gap-2 mb-4">
                                <Sparkles
                                    className="w-4 h-4"
                                    strokeWidth={1.6}
                                />
                                <span className="stat-label">
                                    Moteur d'analyse
                                </span>
                            </div>
                            <div className="space-y-3 text-xs">
                                <div className="flex items-center justify-between border-b border-border pb-2">
                                    <span className="text-ink-soft">
                                        Claude Sonnet 4.5
                                    </span>
                                    <Badge className="rounded-none bg-ok text-white border-ok">
                                        Prêt
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between border-b border-border pb-2">
                                    <span className="text-ink-soft">
                                        GPT-5.2
                                    </span>
                                    <Badge className="rounded-none bg-ok text-white border-ok">
                                        Prêt
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-ink-soft">
                                        Parser PDF
                                    </span>
                                    <Badge className="rounded-none bg-ok text-white border-ok">
                                        Actif
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Projects list */}
            <section
                id="chantiers"
                className="max-w-[1400px] mx-auto px-10 py-12"
            >
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="stat-label mb-2">02 — Chantiers</div>
                        <h2 className="font-heading text-2xl font-bold">
                            {projects.length} chantier
                            {projects.length > 1 ? "s" : ""} suivi
                            {projects.length > 1 ? "s" : ""}
                        </h2>
                    </div>
                </div>

                {loading ? (
                    <div className="text-sm text-ink-soft">Chargement…</div>
                ) : projects.length === 0 ? (
                    <div
                        data-testid="empty-projects"
                        className="border border-dashed border-ink p-12 bg-white text-center"
                    >
                        <Building2
                            className="w-10 h-10 mx-auto mb-4 text-ink-soft"
                            strokeWidth={1.5}
                        />
                        <div className="font-heading text-xl font-bold mb-1">
                            Aucun chantier pour le moment
                        </div>
                        <p className="text-ink-soft text-sm mb-5">
                            Créez votre premier chantier pour commencer à
                            collecter et comparer les devis.
                        </p>
                        <NewProjectDialog onCreated={load} />
                    </div>
                ) : (
                    <div
                        data-testid="projects-grid"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {projects.map((p) => (
                            <ProjectCard key={p.id} project={p} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

function ProjectCard({ project }) {
    const [stats, setStats] = useState({
        devis_count: 0,
        total_ht: 0,
        duplicates: 0,
    });

    useEffect(() => {
        (async () => {
            try {
                const [devis, dups] = await Promise.all([
                    api.listDevis(project.id),
                    api.listDuplicates(project.id),
                ]);
                const totalHt = devis.reduce(
                    (acc, d) => acc + (d.total_ht || 0),
                    0
                );
                setStats({
                    devis_count: devis.length,
                    total_ht: totalHt,
                    duplicates: dups.length,
                });
            } catch (e) {
                console.error("ProjectsPage card stats failed:", e);
            }
        })();
    }, [project.id]);

    return (
        <Link
            to={`/chantiers/${project.id}`}
            data-testid={`project-card-${project.id}`}
            className="block group panel hover:border-ink transition-colors"
        >
            <div className="p-5 border-b border-border flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="font-heading font-bold text-lg leading-tight truncate">
                        {project.name}
                    </div>
                    <div className="text-xs text-ink-soft mt-1 truncate">
                        {project.address || "—"}
                    </div>
                </div>
                <ArrowUpRight
                    className="w-5 h-5 shrink-0 text-ink-soft group-hover:text-accent transition-colors"
                    strokeWidth={1.5}
                />
            </div>
            <div className="px-5 py-4 grid grid-cols-3 gap-2 text-left">
                <div>
                    <div className="stat-label">Devis</div>
                    <div className="num text-lg font-bold mt-0.5 flex items-center gap-1.5">
                        <FileStack
                            className="w-3.5 h-3.5 text-ink-soft"
                            strokeWidth={1.6}
                        />
                        {stats.devis_count}
                    </div>
                </div>
                <div>
                    <div className="stat-label">Total HT</div>
                    <div className="num text-sm font-semibold mt-0.5">
                        {fmtEUR(stats.total_ht)}
                    </div>
                </div>
                <div>
                    <div className="stat-label">Doublons</div>
                    <div
                        className={`num text-lg font-bold mt-0.5 flex items-center gap-1.5 ${
                            stats.duplicates > 0 ? "text-warn" : ""
                        }`}
                    >
                        {stats.duplicates > 0 && (
                            <AlertTriangle
                                className="w-3.5 h-3.5"
                                strokeWidth={2}
                            />
                        )}
                        {stats.duplicates}
                    </div>
                </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-between text-xs">
                <span className="pill border-ink/30 bg-[#FAFAFA] text-ink-soft">
                    {STATUS_LABEL[project.status] || project.status}
                </span>
                <span className="text-ink-soft">
                    {fmtDate(project.created_at)}
                </span>
            </div>
        </Link>
    );
}
